// @ts-nocheck
// App.jsx — KitchenControl schil
// Modules: HACCP + MEP
// Iconen: lucide-react

import { useState, useEffect } from "react";
import {
  ClipboardList,
  ShieldCheck,
  Sparkles,
  Thermometer,
  PackageCheck,
  Snowflake,
  TriangleAlert,
  Siren,
  HandHelping,
  Settings,
  Menu,
  Building2,
  FolderPlus,
  PlusCircle,
  FileText,
  Archive,
  ChevronRight,
  ChevronDown,
  BookOpen,
  ClipboardPaste,
  FolderCog,
  Users,
} from "lucide-react";
import HaccpModule from "./HaccpModule";
import TeamModule from "./TeamModule";
import ReceptenModule from "./ReceptenModule";
import MepModule from "./MepModule";

// ══════════════════════════════════════════════════════════════════════════════
// CONFIG
// ══════════════════════════════════════════════════════════════════════════════
const CONFIG = {
  supabaseUrl: "https://vvwgplljrzeycxlorfli.supabase.co",
  supabaseKey: "sb_publishable_zETvKuK1K2Lhh2waE5uoPw_seq-Ptf_",
  tenantId: "9429d24f-ae06-4c65-8825-636d197bb807",
};

// ── Supabase sync-laag ────────────────────────────────────────────────────────
const isJwt = (k) => typeof k === "string" && k.startsWith("eyJ");

async function SB(method, table, params, body) {
  const { supabaseUrl: url, supabaseKey: key } = CONFIG;
  if (!key) { console.warn("[SB] Geen supabaseKey ingesteld."); return null; }
  const hdrs = { "Content-Type": "application/json", apikey: key };
  if (isJwt(key)) hdrs["Authorization"] = `Bearer ${key}`;
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  try {
    const r = await fetch(`${url}/rest/v1/${table}${qs}`, {
      method, headers: hdrs,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) { console.error(`[SB] ${method} ${table} → ${r.status}`, await r.text()); return null; }
    if (method === "GET") return r.json();
    return true;
  } catch (e) { console.error("[SB] fout:", e); return null; }
}

// ── Kleuren (gedeeld palet) ───────────────────────────────────────────────────
const P = {
  bg: "#F8FAFC",
  navy: "#0F172A",
  navyMid: "#1E293B",
  navyLt: "#334155",
  txt: "#F1F5F9",
  txtMut: "#94A3B8",
  pri: "#2563EB",
  priBg: "#EFF6FF",
  sep: "#1E293B",
};

// ── Module-definities ─────────────────────────────────────────────────────────
const MODULE_DEFS = [
  {
    id: "mep",
    label: "MEP",
    Icon: ClipboardList,
    echt: true,
    subItems: [
      { id: "lijst",         Icon: ClipboardList, label: "Lijst" },
      { id: "afdeling",      Icon: Building2,     label: "Nieuwe afdeling" },
      { id: "categorie",     Icon: FolderPlus,    label: "Nieuwe categorie" },
      { id: "nieuwitem",     Icon: PlusCircle,    label: "Nieuw item" },
      { id: "bestelbon",     Icon: FileText,      label: "Bestelbon" },
      { id: "archief",       Icon: Archive,       label: "Archief" },
    ],
  },
  {
    id: "recepten",
    label: "Recepten",
    Icon: BookOpen,
    echt: true,
    subItems: [
      { id: "recepten",    Icon: BookOpen,        label: "Recepten" },
      { id: "nieuw",       Icon: PlusCircle,      label: "Nieuw recept" },
      { id: "paste",       Icon: ClipboardPaste,  label: "Recept plakken" },
      { id: "categorieen", Icon: FolderCog,       label: "Categorieën" },
    ],
  },
  {
    id: "team",
    label: "Team",
    Icon: Users,
    echt: true,
    subItems: [],
  },
  {
    id: "haccp",
    label: "HACCP",
    Icon: ShieldCheck,
    echt: true,
    subItems: [
      { id: "schoonmaak",   Icon: Sparkles,       label: "Schoonmaak" },
      { id: "temp",         Icon: Thermometer,    label: "Temperatuur" },
      { id: "ontvangst",    Icon: PackageCheck,   label: "Ontvangst" },
      { id: "terugkoel",    Icon: Snowflake,      label: "Terugkoelen" },
      { id: "allergen",     Icon: TriangleAlert,  label: "Allergenen" },
      { id: "afwijking",    Icon: Siren,          label: "Afwijkingen" },
      { id: "hygiene",      Icon: HandHelping,    label: "Hygiëne" },
      { id: "instellingen", Icon: Settings,       label: "Instellingen", beheer: true },
    ],
  },
];

// ── Sub-item stijl ────────────────────────────────────────────────────────────
function SubItem({ s, active, onClick }) {
  const Icon = s.Icon;
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "7px 12px 7px 32px",
        marginBottom: 1,
        borderRadius: 7,
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        fontSize: 12.5,
        background: active ? P.navyMid : "transparent",
        color: active ? "#fff" : P.txtMut,
        fontWeight: active ? 600 : 400,
        borderLeft: active ? `2px solid ${P.pri}` : "2px solid transparent",
        transition: "background 0.12s, color 0.12s",
      }}
    >
      <Icon size={14} strokeWidth={active ? 2.5 : 2} style={{ flexShrink: 0, opacity: active ? 1 : 0.7 }} />
      {s.label}
    </button>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [subView, setSubView] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [overlayActief, setOverlayActief] = useState(false);
  const [openReceptId, setOpenReceptId] = useState(null);
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const check = () => setNarrow(window.innerWidth <= 720);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    (async () => {
      const rows = await SB("GET", "app_config", {
        tenant_id: `eq.${CONFIG.tenantId}`,
        select: "*",
        limit: "1",
      });
      setConfig(rows?.length ? rows[0] : {
        tenant_id: CONFIG.tenantId,
        bedrijfsnaam: "KitchenControl",
        modules: {},
      });
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!loading && !activeModule) {
      const first = MODULE_DEFS.find((m) => m.echt);
      if (first) setActiveModule(first.id);
    }
  }, [loading]);

  useEffect(() => {
    const def = MODULE_DEFS.find((m) => m.id === activeModule);
    if (def?.subItems?.length) {
      const geldig = def.subItems.some((s) => s.id === subView);
      if (!geldig) setSubView(def.subItems[0].id);
    } else {
      setSubView(null);
    }
  }, [activeModule]);

  const handleConfigSave = async (newConfig) => {
    setConfig(newConfig);
    await SB("PATCH", "app_config", { tenant_id: `eq.${CONFIG.tenantId}` }, newConfig);
  };

  const modules = MODULE_DEFS.filter((m) => {
    if (config?.modules && Object.keys(config.modules).length > 0)
      return config.modules[m.id] !== false;
    return true;
  });

  const bedrijfsnaam = config?.bedrijfsnaam || "KitchenControl";
  const logoUrl = config?.logo_url || "";

  return (
    <div style={{ display: "flex", height: "100vh", background: P.bg, fontFamily: "system-ui,-apple-system,sans-serif", overflow: "hidden" }}>

      {/* ── Mobiele topbalk ── */}
      {narrow && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 48, background: P.navy, display: "flex", alignItems: "center", padding: "0 12px", gap: 12, zIndex: 100, borderBottom: `1px solid ${P.sep}` }}>
          <button onClick={() => { setSideOpen(s => { if (!s) { setTimeout(() => setOverlayActief(true), 300); } else { setOverlayActief(false); } return !s; }); }} style={{ background: "none", border: "none", color: P.txt, cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}>
            <Menu size={20} />
          </button>
          <span style={{ fontSize: 15, fontWeight: 700, color: P.txt, letterSpacing: 0.5 }}>
            {logoUrl ? <img src={logoUrl} alt={bedrijfsnaam} style={{ height: 26, objectFit: "contain" }} /> : bedrijfsnaam}
          </span>
          {activeModule && (
            <span style={{ fontSize: 12, color: P.txtMut }}>
              · {MODULE_DEFS.find((m) => m.id === activeModule)?.label}
            </span>
          )}
        </div>
      )}

      {/* ── Overlay (mobiel) ── */}
      {narrow && sideOpen && (
        <div onClick={() => { if (overlayActief) { setSideOpen(false); setOverlayActief(false); } }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 150, pointerEvents: overlayActief ? "auto" : "none" }} />
      )}

      {/* ── Zijbalk ── */}
      <div style={{
        width: 220,
        background: P.navy,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        ...(narrow ? {
          position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 200,
          transform: sideOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.22s ease",
        } : {}),
      }}>
        {/* Logo/naam */}
        <div style={{ padding: "20px 16px 14px", borderBottom: `1px solid ${P.sep}` }}>
          {logoUrl
            ? <img src={logoUrl} alt={bedrijfsnaam} style={{ height: 32, objectFit: "contain" }} />
            : <span style={{ fontSize: 17, fontWeight: 800, color: P.txt, letterSpacing: 0.5 }}>{bedrijfsnaam}</span>
          }
        </div>

        {/* Module-navigatie */}
        <nav style={{ flex: 1, overflowY: "auto", padding: "10px 8px" }}>
          {modules.map((m) => {
            const active = activeModule === m.id;
            const Icon = m.Icon;
            const subs = active ? m.subItems || [] : [];
            const hoofd = subs.filter((s) => !s.beheer);
            const beheer = subs.filter((s) => s.beheer);

            return (
              <div key={m.id}>
                {/* Hoofd-module knop */}
                <button
                  onClick={() => {
                    setActiveModule(m.id);
                    // Mobiel + heeft sub-items: menu open houden zodat gebruiker sub-item kan kiezen
                    if (!narrow || !m.subItems?.length) setSideOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: 8,
                    background: active ? P.pri : "transparent",
                    border: "none",
                    color: active ? "#fff" : P.txtMut,
                    fontSize: 13,
                    fontFamily: "inherit",
                    fontWeight: active ? 600 : 400,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.12s, color 0.12s",
                    marginBottom: 2,
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = P.navyMid; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                >
                  <Icon size={16} strokeWidth={active ? 2.5 : 2} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{m.label}</span>
                  {m.subItems?.length
                    ? active
                      ? <ChevronDown size={12} style={{ opacity: 0.6 }} />
                      : <ChevronRight size={12} style={{ opacity: 0.4 }} />
                    : null
                  }
                </button>

                {/* Sub-items */}
                {subs.length > 0 && (
                  <div style={{ marginBottom: 4, padding: "2px 0 2px" }}>
                    {hoofd.map((s) => (
                      <SubItem
                        key={s.id}
                        s={s}
                        active={s.id === subView}
                        onClick={() => { setSubView(s.id); if (narrow) { setSideOpen(false); setOverlayActief(false); } }}
                      />
                    ))}
                    {beheer.length > 0 && (
                      <>
                        <div style={{ height: 1, background: P.sep, margin: "6px 12px 6px 32px" }} />
                        {beheer.map((s) => (
                          <SubItem
                            key={s.id}
                            s={s}
                            active={s.id === subView}
                            onClick={() => { setSubView(s.id); if (narrow) { setSideOpen(false); setOverlayActief(false); } }}
                          />
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sync-status onderaan */}
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${P.sep}`, fontSize: 11, color: P.txtMut }}>
          {loading ? "⏳ verbinden…"
            : CONFIG.supabaseKey
              ? <span style={{ color: "#4ADE80" }}>✓ verbonden — {bedrijfsnaam}</span>
              : <span style={{ color: "#FBBF24" }}>⚠ geen sleutel</span>
          }
        </div>
      </div>

      {/* ── Module-inhoud ── */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", ...(narrow ? { marginTop: 48 } : {}) }}>
        {loading ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 14 }}>
            Laden…
          </div>
        ) : activeModule === "team" ? (
          <TeamModule
            activeTab={subView}
            onTabChange={setSubView}
            sb={{
              select: async (table, params = "") => {
                try {
                  const r = await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${params}`, {
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey },
                  });
                  return r.ok ? await r.json() : [];
                } catch { return []; }
              },
              insert: async (table, body) => {
                try {
                  const r = await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey, Prefer: "return=representation" },
                    body: JSON.stringify(body),
                  });
                  if (!r.ok) return null;
                  const d = await r.json();
                  return Array.isArray(d) ? d[0] : d;
                } catch { return null; }
              },
              update: async (table, filter, body) => {
                try {
                  await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${filter}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey, Prefer: "return=minimal" },
                    body: JSON.stringify(body),
                  });
                } catch {}
              },
              remove: async (table, filter) => {
                try {
                  await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${filter}`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey },
                  });
                } catch {}
              },
            }}
            tenantId={CONFIG.tenantId}
          />
        ) : activeModule === "haccp" ? (
          <HaccpModule
            sb={{
              select: async (table, params = "") => {
                try {
                  const r = await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${params}`, {
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey },
                  });
                  return r.ok ? await r.json() : [];
                } catch { return []; }
              },
              insert: async (table, body) => {
                try {
                  const r = await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey, Prefer: "return=representation" },
                    body: JSON.stringify(body),
                  });
                  if (!r.ok) return null;
                  const d = await r.json();
                  return Array.isArray(d) ? d[0] : d;
                } catch { return null; }
              },
              update: async (table, filter, body) => {
                try {
                  await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${filter}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey, Prefer: "return=minimal" },
                    body: JSON.stringify(body),
                  });
                } catch {}
              },
              remove: async (table, filter) => {
                try {
                  await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${filter}`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey },
                  });
                } catch {}
              },
            }}
            tenantId={CONFIG.tenantId}
            activeTab={subView}
            onTabChange={setSubView}
          />
        ) : activeModule === "recepten" ? (
          <ReceptenModule
            activeTab={subView}
            onTabChange={setSubView}
            sb={{
              select: async (table, params = "") => {
                try {
                  const r = await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${params}`, {
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey },
                  });
                  return r.ok ? await r.json() : [];
                } catch { return []; }
              },
              insert: async (table, body) => {
                try {
                  const r = await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey, Prefer: "return=representation" },
                    body: JSON.stringify(body),
                  });
                  if (!r.ok) return null;
                  const d = await r.json();
                  return Array.isArray(d) ? d[0] : d;
                } catch { return null; }
              },
              update: async (table, filter, body) => {
                try {
                  await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${filter}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey, Prefer: "return=minimal" },
                    body: JSON.stringify(body),
                  });
                } catch {}
              },
              remove: async (table, filter) => {
                try {
                  await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${filter}`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey },
                  });
                } catch {}
              },
            }}
            tenantId={CONFIG.tenantId}
            openReceptId={openReceptId}
            onOpenReceptHandled={() => setOpenReceptId(null)}
          />
        ) : activeModule === "mep" ? (
          <MepModule
            config={config}
            synced={!loading}
            onConfigSave={handleConfigSave}
            activeTab={subView}
            onTabChange={setSubView}
            onOpenRecept={(receptId) => {
              setOpenReceptId(receptId);
              setActiveModule("recepten");
              setSubView("recepten");
            }}
            sb={{
              select: async (table, params = "") => {
                try {
                  const r = await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${params}`, {
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey },
                  });
                  return r.ok ? await r.json() : [];
                } catch { return []; }
              },
              insert: async (table, body) => {
                try {
                  const r = await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey, Prefer: "return=representation" },
                    body: JSON.stringify(body),
                  });
                  if (!r.ok) return null;
                  const d = await r.json();
                  return Array.isArray(d) ? d[0] : d;
                } catch { return null; }
              },
              update: async (table, filter, body) => {
                try {
                  await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${filter}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey, Prefer: "return=minimal" },
                    body: JSON.stringify(body),
                  });
                } catch {}
              },
              remove: async (table, filter) => {
                try {
                  await fetch(`${CONFIG.supabaseUrl}/rest/v1/${table}?${filter}`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json", apikey: CONFIG.supabaseKey },
                  });
                } catch {}
              },
            }}
            tenantId={CONFIG.tenantId}
          />
        ) : (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 14 }}>
            Selecteer een module.
          </div>
        )}
      </div>
    </div>
  );
}
